import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FileText, Plus, CheckCircle2, XCircle, Clock,
  Upload, Eye, AlertTriangle, Search
} from 'lucide-react';

const DOC_GROUPS = [
  'O&M Manuals', 'Warranties', 'COCs / Certificates', 'As-Built Drawings',
  'Test Certificates', 'Commissioning Reports', 'Authority Approvals',
  'Training Records', 'Maintenance Info', 'Approved Shop Drawings', 'General',
];

const STATUS_COLORS = {
  required: 'bg-amber-100 text-amber-700', requested: 'bg-blue-100 text-blue-700',
  submitted: 'bg-purple-100 text-purple-700', under_review: 'bg-indigo-100 text-indigo-700',
  approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700',
  not_required: 'bg-slate-100 text-slate-500', expired: 'bg-red-100 text-red-600',
};

export default function CloseoutDocuments({ projectId, settings }) {
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({
    document_type: '', document_group: 'General', package_id: '',
    required_from_company: '', mandatory_flag: true, trade_code: '',
    required_by_date: '',
  });

  const loadDocs = useCallback(async () => {
    try {
      const res = await api.closeout.listDocuments(projectId);
      setDocs(res.data.items || []);
      setStats(res.data.stats || {});
    } catch { /* silent */ } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => { api.closeout.listPackages(projectId).then(r => setPackages(r.data)).catch(() => {}); }, [projectId]);

  const handleCreate = async () => {
    if (!form.document_type.trim()) { toast.error('Document type required'); return; }
    try {
      const payload = { ...form };
      if (!payload.package_id) delete payload.package_id;
      await api.closeout.createDocument(projectId, payload);
      toast.success('Document requirement created');
      setShowCreate(false);
      setForm({ document_type: '', document_group: 'General', package_id: '', required_from_company: '', mandatory_flag: true, trade_code: '', required_by_date: '' });
      loadDocs();
    } catch { toast.error('Failed to create'); }
  };

  const handleStatusUpdate = async (docId, status) => {
    try {
      await api.closeout.updateDocStatus(docId, status);
      toast.success(`Status updated to ${status}`);
      loadDocs();
    } catch { toast.error('Update failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  // Group by document_group
  const grouped = {};
  docs.forEach(d => { const g = d.document_group || 'General'; grouped[g] = grouped[g] || []; grouped[g].push(d); });

  return (
    <div className="space-y-4" data-testid="closeout-documents">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">
          Close-Out Documents
          <span className="ml-2 text-sm font-normal text-slate-400">({stats.total || 0} total, {stats.mandatory_outstanding || 0} mandatory outstanding)</span>
        </h2>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={() => setShowCreate(true)} data-testid="create-doc-btn">
          <Plus className="h-4 w-4" /> Add Requirement
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{stats.total || 0}</p><p className="text-[10px] text-slate-500 uppercase">Total</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-600">{stats.approved || 0}</p><p className="text-[10px] text-slate-500 uppercase">Approved</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-amber-600">{stats.outstanding || 0}</p><p className="text-[10px] text-slate-500 uppercase">Outstanding</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-red-600">{stats.mandatory_outstanding || 0}</p><p className="text-[10px] text-slate-500 uppercase">Mandatory Missing</p></CardContent></Card>
      </div>

      {/* Grouped Documents */}
      {Object.keys(grouped).length === 0 ? (
        <Card><CardContent className="p-12 text-center"><FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-lg font-medium text-slate-500">No document requirements yet</p></CardContent></Card>
      ) : (
        Object.entries(grouped).map(([group, items]) => (
          <Card key={group}>
            <CardContent className="p-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{group} ({items.length})</h3>
              <div className="space-y-1.5">
                {items.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 border border-slate-100" data-testid={`doc-${doc.id}`}>
                    <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{doc.document_type}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        {doc.required_from_company && <span>From: {doc.required_from_company}</span>}
                        {doc.trade_code && <span>{doc.trade_code}</span>}
                        {doc.mandatory_flag && <Badge className="bg-red-50 text-red-600 text-[9px]">Mandatory</Badge>}
                      </div>
                    </div>
                    <Badge className={`text-[10px] ${STATUS_COLORS[doc.status] || 'bg-slate-100'}`}>{doc.status?.replace(/_/g, ' ')}</Badge>
                    <div className="flex gap-1">
                      {doc.status === 'submitted' && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 text-green-600" onClick={() => handleStatusUpdate(doc.id, 'approved')}><CheckCircle2 className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 text-red-500" onClick={() => handleStatusUpdate(doc.id, 'rejected')}><XCircle className="h-3.5 w-3.5" /></Button>
                        </>
                      )}
                      {doc.status === 'required' && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleStatusUpdate(doc.id, 'requested')}>Request</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-barlow font-bold uppercase">Add Document Requirement</DialogTitle>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Document Type *</Label><Input value={form.document_type} onChange={e => setForm(f => ({...f, document_type: e.target.value}))} placeholder="e.g. Electrical COC" data-testid="doc-type-input" /></div>
            <div><Label className="text-xs">Group</Label>
              <Select value={form.document_group} onValueChange={v => setForm(f => ({...f, document_group: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_GROUPS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Required From</Label><Input value={form.required_from_company} onChange={e => setForm(f => ({...f, required_from_company: e.target.value}))} placeholder="Company" /></div>
              <div><Label className="text-xs">Due Date</Label><Input type="date" value={form.required_by_date} onChange={e => setForm(f => ({...f, required_by_date: e.target.value}))} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.mandatory_flag} onChange={e => setForm(f => ({...f, mandatory_flag: e.target.checked}))} className="rounded" /> Mandatory for completion</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleCreate} data-testid="create-doc-submit"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
